import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  TablePagination,
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { downloads } from '../services/auth';
import toast from 'react-hot-toast';

const History: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data: history, isLoading, error, refetch } = useQuery(
    ['history', page, rowsPerPage],
    () => downloads.getHistory(rowsPerPage, page * rowsPerPage),
    {
      keepPreviousData: true,
    }
  );

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === 'failed') return <ErrorIcon sx={{ fontSize: 16 }} />;
    return undefined;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '—';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  
  const handleTranscriptDownload = async (id: number) => {
  try {
    const response = await fetch(
      downloads.downloadTranscript(id),
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      }
    );

    if (!response.ok) throw new Error("Download failed");

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${id}.txt`;
    a.click();

    window.URL.revokeObjectURL(url);

  } catch (err) {
    toast.error('Failed to download transcript');
  }
};

  const handleDelete = async (id: number) => {
    try {
      await downloads.deleteDownload(id);
      toast.success('Deleted');
      refetch();
    } catch {
      toast.error('Delete failed');
    }
  };
  

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load history
          </Alert>
          <Button variant="contained" onClick={() => refetch()}>
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>

        {/* HEADER */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700}>
            Processing History
          </Typography>
          <Typography color="text.secondary">
            View your processed files, transcripts, and downloads
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !history || history.length === 0 ? (
          <Card sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6">No history yet</Typography>
            <Typography color="text.secondary">
              Your processed files will appear here
            </Typography>
          </Card>
        ) : (
          <Stack spacing={2}>

            {history.map((d: any) => (
              <Card key={d.id} sx={{ borderRadius: 3, boxShadow: 2 }}>
                <CardContent>
                  <Stack spacing={2}>

                    {/* TOP */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography fontWeight={600} noWrap sx={{ maxWidth: 300 }}>
                        {d.filename || 'Untitled File'}
                      </Typography>

                      <Chip
                        icon={getStatusIcon(d.status)}
                        label={d.status}
                        color={getStatusColor(d.status)}
                        size="small"
                      />
                    </Stack>

                    {/* META */}
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Chip label={d.operation?.toUpperCase() || '—'} size="small" variant="outlined" />
                      <Chip label={formatFileSize(d.file_size)} size="small" variant="outlined" />
                      {d.language && (
                        <Chip label={`🌍 ${d.language}`} size="small" variant="outlined" />
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(d.created_at)}
                      </Typography>
                    </Stack>

                    {/* SUMMARY PREVIEW */}
                    {d.summary && (
                      <>
                        <Divider />
                        <Typography variant="body2" color="text.secondary">
                          {d.summary.slice(0, 120)}...
                        </Typography>
                      </>
                    )}

                    <Divider />

                    {/* ACTIONS */}
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>

                      {d.status === 'completed' && d.output_url && (
                        <IconButton
                          onClick={() => window.open(d.output_url, "_blank")}
                          color="primary"
                        >
                          <DownloadIcon />
                        </IconButton>
                      )}

                      {d.status === 'completed' && d.has_transcript && (
                        <IconButton onClick={() => handleTranscriptDownload(d.id)} color="secondary">
                          <DescriptionIcon />
                        </IconButton>
                      )}
                   
                      <IconButton onClick={() => handleDelete(d.id)} color="error">
                        <DeleteIcon />
                      </IconButton>

                    </Stack>

                  </Stack>
                </CardContent>
              </Card>
            ))}

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={history.length >= rowsPerPage ? history.length + 1 : history.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e: any) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />

          </Stack>
        )}

      </Box>
    </Container>
  );
};

export default History;